/**
 * Video-votings resource: the votings defined in this silo, one voting by key,
 * and the editorial writes that create and replace them.
 *
 * The reads are public, because the player serves signed-out viewers, so
 * neither hook gates on a session.
 *
 * Both writes send the whole voting, matching the contract — the editor holds
 * the voting it last read and saves it back — and invalidate the two reads that
 * could now be stale.
 */
import type {
    SpaceVotingResults,
    VideoVoting,
    VideoVotingPromptResults,
    VideoVotingWriteInput,
} from '@repo/shared/orpc/procedures/video-votings';
import { applyMyVideoVote, type MyVideoVote } from '@repo/shared/video-voting-results';
import { useApiQueryUtils } from 'system/api-client-context';
import { type Datastore } from 'util/datastore';
import { fireAndForget } from 'util/hooks';

import { fetchProfileAsync } from './profile';
import { siloParamsOf } from './silo-params';
import { useSiloQuery } from './use-silo-query';

export type {
    MyVideoVoteWire,
    SpaceVotingResults,
    VideoVote,
    VideoVoting,
    VideoVotingPromptResults,
    VideoVotingWriteInput,
} from '@repo/shared/orpc/procedures/video-votings';

// One shared empty list, so a still-loading read returns a stable reference
// rather than a fresh array every render.
const NO_VOTINGS: VideoVoting[] = [];

const ALL_VOTINGS_LIMIT = 100000;

/** Every video voting in the silo, oldest first. Empty while loading. */
export function useAllVideoVotings(): { votings: VideoVoting[]; isLoading: boolean } {
    const utils = useApiQueryUtils();
    const query = useSiloQuery({ procedure: utils.videoVotings.list, input: { limit: ALL_VOTINGS_LIMIT } });
    return { votings: query.data?.items ?? NO_VOTINGS, isLoading: query.isLoading };
}

/** One video voting by key, or undefined while loading and when none exists. */
export function useVideoVoting(votingKey: string | null | undefined): VideoVoting | undefined {
    const utils = useApiQueryUtils();
    const query = useSiloQuery({ procedure: utils.videoVotings.get, input: { votingKey: votingKey ?? '' }, enabled: !!votingKey });
    return query.data;
}

/**
 * The votings attached to one story. A story carries at most one, so callers
 * take the first entry rather than rendering a list.
 */
export function useVideoVotingsForStory(storyKey: string | null | undefined): { votings: VideoVoting[]; isLoading: boolean } {
    const utils = useApiQueryUtils();
    const query = useSiloQuery({ procedure: utils.videoVotings.listForStory, input: { storyKey: storyKey ?? '' }, enabled: !!storyKey });
    return { votings: query.data?.items ?? NO_VOTINGS, isLoading: query.isLoading };
}

async function invalidateStoryAsync(datastore: Datastore, storyKey: string): Promise<void> {
    const { siloKey } = siloParamsOf(datastore);
    const queryClient = datastore.getQueryClient();
    const utils = datastore.getApiQueryUtils();
    await queryClient.invalidateQueries({
        queryKey: utils.videoVotings.listForStory.queryOptions({ input: { siloKey, storyKey } }).queryKey,
    });
}

/** Attaches a voting to a story, so its prompts play with that article. */
export async function assignVideoVotingAsync(datastore: Datastore, { votingKey, storyKey }: AssignmentInput): Promise<void> {
    const { siloKey, locale } = siloParamsOf(datastore);
    await datastore.getApiClient().videoVotings.assign({ siloKey, locale, votingKey, storyKey });
    await invalidateStoryAsync(datastore, storyKey);
}

/** Detaches a voting from a story, leaving the votes already cast in place. */
export async function unassignVideoVotingAsync(datastore: Datastore, { votingKey, storyKey }: AssignmentInput): Promise<void> {
    const { siloKey, locale } = siloParamsOf(datastore);
    await datastore.getApiClient().videoVotings.unassign({ siloKey, locale, votingKey, storyKey });
    await invalidateStoryAsync(datastore, storyKey);
}

type AssignmentInput = { votingKey: string; storyKey: string };

const NO_PROMPTS: VideoVotingPromptResults[] = [];
const NO_RESULTS: SpaceVotingResults[] = [];

const ALL_STORIES_LIMIT = 100000;

/**
 * Everything a space's Votings tab renders: its linked stories, each with the
 * voting attached to it and the totals for its prompts.
 */
export function useVideoVotingResultsForSpace(spaceKey: string | null | undefined): {
    results: SpaceVotingResults[];
    isLoading: boolean;
} {
    const utils = useApiQueryUtils();
    const query = useSiloQuery({
        procedure: utils.videoVotings.listForSpace,
        input: { spaceKey: spaceKey ?? '', limit: ALL_STORIES_LIMIT },
        enabled: !!spaceKey,
    });
    return { results: query.data?.items ?? NO_RESULTS, isLoading: query.isLoading };
}

/** The totals for each prompt on a voting, with the viewer's own vote. */
export function useVideoVotingResults(votingKey: string | null | undefined): { prompts: VideoVotingPromptResults[]; isLoading: boolean } {
    const utils = useApiQueryUtils();
    const query = useSiloQuery({ procedure: utils.videoVotings.results, input: { votingKey: votingKey ?? '' }, enabled: !!votingKey });
    return { prompts: query.data?.prompts ?? NO_PROMPTS, isLoading: query.isLoading };
}

type ResultsCache = { prompts: VideoVotingPromptResults[] };

/**
 * Applies the viewer's vote change to the cached totals, then reconciles with
 * the server.
 *
 * The patch is what keeps the dialog responsive. A viewer answers a prompt
 * against a countdown, so the counts move the instant they press rather than
 * after a round trip, and roll back if the server refuses.
 */
async function patchMyVoteAsync(
    datastore: Datastore,
    { votingKey, questionKey, next }: { votingKey: string; questionKey: string; next: MyVideoVote | null },
    write: () => Promise<void>,
): Promise<void> {
    const { siloKey, locale } = siloParamsOf(datastore);
    const queryClient = datastore.getQueryClient();
    const utils = datastore.getApiQueryUtils();
    const resultsKey = utils.videoVotings.results.queryOptions({ input: { siloKey, locale, votingKey } }).queryKey;
    const myKey = datastore.getPersonaKey();
    const me = next && !next.anonymous ? await fetchProfileAsync(datastore, myKey) : null;

    await queryClient.cancelQueries({ queryKey: resultsKey });
    const previous = queryClient.getQueryData<ResultsCache>(resultsKey);
    if (previous) {
        queryClient.setQueryData<ResultsCache>(resultsKey, {
            prompts: previous.prompts.map((prompt) =>
                prompt.questionKey === questionKey
                    ? applyMyVideoVote(prompt, { next, me, isMe: (voter) => voter.profileKey === myKey })
                    : prompt,
            ),
        });
    }

    try {
        await write();
    } catch (err) {
        queryClient.setQueryData(resultsKey, previous);
        throw err;
    } finally {
        fireAndForget(queryClient.invalidateQueries({ queryKey: resultsKey }));
    }
}

type SetVoteInput = { votingKey: string; questionKey: string; optionKey?: string | null; value?: number | null; anonymous?: boolean };

/** Casts or changes the caller's vote on one prompt. */
export async function setVideoVoteAsync(datastore: Datastore, input: SetVoteInput): Promise<void> {
    const { siloKey, locale } = siloParamsOf(datastore);
    const { votingKey, questionKey, optionKey = null, value = null, anonymous = false } = input;

    await patchMyVoteAsync(datastore, { votingKey, questionKey, next: { optionKey, value, anonymous } }, async () => {
        await datastore.getApiClient().videoVotings.setVote({ siloKey, locale, ...input });
    });
}

/** Removes the caller's vote on one prompt, if they cast one. */
export async function removeVideoVoteAsync(datastore: Datastore, { votingKey, questionKey }: RemoveVoteInput): Promise<void> {
    const { siloKey, locale } = siloParamsOf(datastore);

    await patchMyVoteAsync(datastore, { votingKey, questionKey, next: null }, async () => {
        await datastore.getApiClient().videoVotings.removeVote({ siloKey, locale, votingKey, questionKey });
    });
}

type RemoveVoteInput = { votingKey: string; questionKey: string };

/**
 * Whether the viewer has switched video-voting prompts off for every video.
 * Defaults to false while loading, so a slow read shows prompts rather than
 * hiding a feature the viewer never turned off.
 */
export function useVideoVotingDisabled(): boolean {
    const utils = useApiQueryUtils();
    const query = useSiloQuery({ procedure: utils.videoVotings.myPreferences });
    return query.data?.votingDisabled ?? false;
}

/** Switches video-voting prompts off, or back on, for every video. */
export async function setVideoVotingDisabledAsync(datastore: Datastore, votingDisabled: boolean): Promise<void> {
    const { siloKey, locale } = siloParamsOf(datastore);
    const queryClient = datastore.getQueryClient();
    const utils = datastore.getApiQueryUtils();
    const preferencesKey = utils.videoVotings.myPreferences.queryOptions({ input: { siloKey } }).queryKey;

    await datastore.getApiClient().videoVotings.setMyPreferences({ siloKey, locale, votingDisabled });
    await queryClient.invalidateQueries({ queryKey: preferencesKey });
}

async function invalidateVotingAsync(datastore: Datastore, votingKey: string): Promise<void> {
    const { siloKey } = siloParamsOf(datastore);
    const queryClient = datastore.getQueryClient();
    const utils = datastore.getApiQueryUtils();
    await Promise.all([
        queryClient.invalidateQueries({ queryKey: utils.videoVotings.list.queryOptions({ input: { siloKey } }).queryKey }),
        queryClient.invalidateQueries({ queryKey: utils.videoVotings.get.queryOptions({ input: { siloKey, votingKey } }).queryKey }),
    ]);
}

/** Creates a voting and returns it, including the key the server assigned. */
export async function createVideoVotingAsync(datastore: Datastore, input: VideoVotingWriteInput): Promise<VideoVoting> {
    const { siloKey, locale } = siloParamsOf(datastore);
    const created = await datastore.getApiClient().videoVotings.create({ siloKey, locale, ...input });
    await invalidateVotingAsync(datastore, created.votingKey);
    return created;
}

/** Replaces a voting wholesale, `name` included, returning what was stored. */
export async function editVideoVotingAsync(
    datastore: Datastore,
    { votingKey, ...input }: VideoVotingWriteInput & { votingKey: string },
): Promise<VideoVoting> {
    const { siloKey, locale } = siloParamsOf(datastore);
    const saved = await datastore.getApiClient().videoVotings.edit({ siloKey, locale, votingKey, ...input });
    await invalidateVotingAsync(datastore, votingKey);
    return saved;
}
